import { NumberInput, Select } from '@mantine/core';
import { useEffect, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { useEventContext } from 'Screens/Dashboard/Events/EventHandler/EntityEventContext';
import styles from './EventReminderInput.module.scss';

const MAX_REMINDER_MINUTES = 43200;

type ReminderMode = 'none' | 'start' | '10' | '30' | '60' | '1440' | 'custom';
type ReminderUnit = 'minutes' | 'hours' | 'days';

const presetMinuteValues = new Set([0, 10, 30, 60, 1440]);

const getReminderMode = (value?: number | null): ReminderMode => {
    if (value === null || value === undefined) {
        return 'none';
    }

    if (presetMinuteValues.has(value)) {
        return value === 0 ? 'start' : `${value}` as ReminderMode;
    }

    return 'custom';
};

const getUnitMultiplier = (unit: ReminderUnit) => {
    if (unit === 'days') {
        return 1440;
    }

    if (unit === 'hours') {
        return 60;
    }

    return 1;
};

const getMaxForUnit = (unit: ReminderUnit) => MAX_REMINDER_MINUTES / getUnitMultiplier(unit);

const getCustomParts = (value?: number | null): { amount: number; unit: ReminderUnit } => {
    const minutes = typeof value === 'number' && value >= 0 ? value : 10;

    if (minutes > 0 && minutes % 1440 === 0) {
        return {
            amount: minutes / 1440,
            unit: 'days',
        };
    }

    if (minutes > 0 && minutes % 60 === 0) {
        return {
            amount: minutes / 60,
            unit: 'hours',
        };
    }

    return {
        amount: minutes,
        unit: 'minutes',
    };
};

const EventReminderInput = () => {
    const intl = useIntl();
    const { values, updateValues } = useEventContext();
    const [isCustomMode, setIsCustomMode] = useState(false);
    const reminderValue = values.attendeeReminderMinutesBefore ?? null;
    const reminderMode = isCustomMode ? 'custom' : getReminderMode(reminderValue);
    const customParts = getCustomParts(reminderValue);
    const hasError = reminderValue !== null &&
        (reminderValue < 0 || reminderValue > MAX_REMINDER_MINUTES);

    useEffect(() => {
        if (reminderValue === null) {
            setIsCustomMode(false);
        }
    }, [reminderValue]);

    const reminderOptions = [
        { value: 'none', label: intl.formatMessage(messages.none) },
        { value: 'start', label: intl.formatMessage(messages.atStart) },
        { value: '10', label: intl.formatMessage(messages.tenMinutes) },
        { value: '30', label: intl.formatMessage(messages.thirtyMinutes) },
        { value: '60', label: intl.formatMessage(messages.oneHour) },
        { value: '1440', label: intl.formatMessage(messages.oneDay) },
        { value: 'custom', label: intl.formatMessage(messages.custom) },
    ];

    const unitOptions = [
        { value: 'minutes', label: intl.formatMessage(messages.minutes) },
        { value: 'hours', label: intl.formatMessage(messages.hours) },
        { value: 'days', label: intl.formatMessage(messages.days) },
    ];

    const updateReminderMode = (mode: string | null) => {
        if (!mode || mode === 'none') {
            setIsCustomMode(false);
            updateValues({ attendeeReminderMinutesBefore: null });
            return;
        }

        if (mode === 'start') {
            setIsCustomMode(false);
            updateValues({ attendeeReminderMinutesBefore: 0 });
            return;
        }

        if (mode === 'custom') {
            setIsCustomMode(true);
            updateValues({ attendeeReminderMinutesBefore: customParts.amount * getUnitMultiplier(customParts.unit) });
            return;
        }

        setIsCustomMode(false);
        updateValues({ attendeeReminderMinutesBefore: Number(mode) });
    };

    const updateCustomAmount = (value: number | string) => {
        const amount = Math.min(getMaxForUnit(customParts.unit), Math.max(0, Number(value) || 0));
        updateValues({
            attendeeReminderMinutesBefore: amount * getUnitMultiplier(customParts.unit),
        });
    };

    const updateCustomUnit = (unit: string | null) => {
        const nextUnit = (unit ?? 'minutes') as ReminderUnit;
        const nextAmount = Math.min(customParts.amount, getMaxForUnit(nextUnit));

        updateValues({
            attendeeReminderMinutesBefore: nextAmount * getUnitMultiplier(nextUnit),
        });
    };

    return (
        <div className={styles.container}>
            <Select
                id="event-reminder-input"
                label={intl.formatMessage(messages.label)}
                data={reminderOptions}
                value={reminderMode}
                onChange={updateReminderMode}
                allowDeselect={false}
                radius="md"
                size="md"
                comboboxProps={{ withinPortal: false }}
                error={hasError ? intl.formatMessage(messages.error) : undefined}
            />
            {reminderMode === 'custom' && (
                <div className={styles.customReminderRow}>
                    <NumberInput
                        id="event-reminder-custom-amount-input"
                        label={intl.formatMessage(messages.customAmount)}
                        value={customParts.amount}
                        onChange={updateCustomAmount}
                        min={0}
                        max={getMaxForUnit(customParts.unit)}
                        allowDecimal={false}
                        radius="md"
                        size="md"
                        error={hasError ? intl.formatMessage(messages.error) : undefined}
                    />
                    <Select
                        id="event-reminder-custom-unit-input"
                        label={intl.formatMessage(messages.customUnit)}
                        data={unitOptions}
                        value={customParts.unit}
                        onChange={updateCustomUnit}
                        allowDeselect={false}
                        radius="md"
                        size="md"
                        comboboxProps={{ withinPortal: false }}
                    />
                </div>
            )}
        </div>
    );
};

const messages = defineMessages({
    label: {
        id: 'src.Screens.Dashboard.Events.EventHandler.inputs.EventReminderInput.label',
        defaultMessage: 'Przypomnienie',
    },
    none: {
        id: 'src.Screens.Dashboard.Events.EventHandler.inputs.EventReminderInput.none',
        defaultMessage: 'Brak przypomnienia',
    },
    atStart: {
        id: 'src.Screens.Dashboard.Events.EventHandler.inputs.EventReminderInput.atStart',
        defaultMessage: 'W momencie wydarzenia',
    },
    tenMinutes: {
        id: 'src.Screens.Dashboard.Events.EventHandler.inputs.EventReminderInput.tenMinutes',
        defaultMessage: '10 minut wcześniej',
    },
    thirtyMinutes: {
        id: 'src.Screens.Dashboard.Events.EventHandler.inputs.EventReminderInput.thirtyMinutes',
        defaultMessage: '30 minut wcześniej',
    },
    oneHour: {
        id: 'src.Screens.Dashboard.Events.EventHandler.inputs.EventReminderInput.oneHour',
        defaultMessage: '1 godzinę wcześniej',
    },
    oneDay: {
        id: 'src.Screens.Dashboard.Events.EventHandler.inputs.EventReminderInput.oneDay',
        defaultMessage: '1 dzień wcześniej',
    },
    custom: {
        id: 'src.Screens.Dashboard.Events.EventHandler.inputs.EventReminderInput.custom',
        defaultMessage: 'Własny termin',
    },
    customAmount: {
        id: 'src.Screens.Dashboard.Events.EventHandler.inputs.EventReminderInput.customAmount',
        defaultMessage: 'Liczba',
    },
    customUnit: {
        id: 'src.Screens.Dashboard.Events.EventHandler.inputs.EventReminderInput.customUnit',
        defaultMessage: 'Jednostka',
    },
    minutes: {
        id: 'src.Screens.Dashboard.Events.EventHandler.inputs.EventReminderInput.minutes',
        defaultMessage: 'Minuty',
    },
    hours: {
        id: 'src.Screens.Dashboard.Events.EventHandler.inputs.EventReminderInput.hours',
        defaultMessage: 'Godziny',
    },
    days: {
        id: 'src.Screens.Dashboard.Events.EventHandler.inputs.EventReminderInput.days',
        defaultMessage: 'Dni',
    },
    error: {
        id: 'src.Screens.Dashboard.Events.EventHandler.inputs.EventReminderInput.error',
        defaultMessage: 'Przypomnienie nie może być ujemne ani większe niż 30 dni przed wydarzeniem.',
    },
});

export default EventReminderInput;
